import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Box, Loader, Paper, Stack, Text } from '@mantine/core'
import { supabase } from '@/lib/supabase'
import { getDeviceId } from '@/lib/deviceId'

export const Route = createFileRoute('/guest/logged_in/$pin')({
  component: LoggedInGuestLayout,
})

function LoggedInGuestLayout() {
  const params = Route.useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    const validatePin = async () => {
      setLoading(true)

      try {
        const deviceId = getDeviceId()

        // Use the database function to validate PIN (handles lockout server-side)
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          'validate_guest_pin_with_lockout',
          {
            p_pin: params.pin,
            p_device_id: deviceId,
            p_lockout_type: 'guest_pin_lockout',
          }
        )

        if (rpcError) {
          console.error('Error validating PIN:', rpcError)
          navigate({
            to: '/guest/pin',
            search: {
              pin: undefined,
            },
          })
          return
        }

        // Type assertion for the JSONB response
        const data = rpcData as {
          success: boolean
          error?: 'LOCKED_OUT' | 'INVALID_PIN'
          lockout_until?: string
          remaining_seconds?: number
          guest_id?: string
          primary_name?: string
        }

        if (data.success) {
          // PIN is valid
          setIsValid(true)
        } else {
          // Invalid PIN or locked out - redirect to PIN page
          // The lockout is already recorded in the database function
          navigate({
            to: '/guest/pin',
            search: {
              pin: undefined,
            },
          })
        }
      } catch (err) {
        console.error('Error validating PIN:', err)
        navigate({
          to: '/guest/pin',
          search: {
            pin: undefined,
          },
        })
      } finally {
        setLoading(false)
      }
    }

    validatePin()
  }, [params.pin, navigate])

  if (loading) {
    return (
      <Box className="guest-layout">
        <Paper shadow="xl" p="xl" style={{ maxWidth: 400, margin: '2rem auto' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="lg" fw={500} c="golden">
              Lädt...
            </Text>
          </Stack>
        </Paper>
      </Box>
    )
  }

  if (!isValid) {
    return null
  }

  return <Outlet />
}
